import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, Button } from '../ui';
import { Line, LineChart, ResponsiveContainer, YAxis } from 'recharts';
import { ArrowRight } from 'lucide-react';
import type { CompetitorPositionHistoryPoint, KeywordPositionsResult, YandexCompetitorPositionsData } from '../../lib/api';

interface CompetitorsSectionProps {
  businessId: string;
  businessName: string;
  data: YandexCompetitorPositionsData | null;
  history: CompetitorPositionHistoryPoint[];
  keywordPositions: KeywordPositionsResult | null;
  loading: boolean;
}

function KeywordDelta({ delta }: { delta: number | null }) {
  if (delta === null) return <span className="text-gray-300 text-xs">—</span>;
  if (delta < 0) return <span className="text-green-600 text-xs font-medium">▲ {Math.abs(delta)}</span>;
  if (delta > 0) return <span className="text-red-500 text-xs font-medium">▼ {delta}</span>;
  return <span className="text-gray-400 text-xs">без изменений</span>;
}

export function CompetitorsSection({ businessId, businessName, data, history, keywordPositions, loading }: CompetitorsSectionProps) {
  const navigate = useNavigate();
  const chartData = history.filter((p) => p.position !== null).map((p) => ({ value: p.position as number }));

  return (
    <Card>
      <CardHeader title="Место среди конкурентов" />

      {loading ? (
        <div className="h-40 flex items-center justify-center text-gray-400 text-sm">Загрузка...</div>
      ) : !data ? (
        <div className="h-40 flex items-center justify-center text-gray-400 text-sm">Нет данных — обновите позиции на вкладке Аналитика</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-3xl font-bold text-gray-900">
              {data.ownPosition !== null ? `#${data.ownPosition}` : '—'}
            </p>
            <p className="text-sm text-gray-500 mt-1">в выдаче по запросу «{data.query}»</p>
            {chartData.length >= 2 && (
              <div className="mt-3">
                <ResponsiveContainer width="100%" height={48}>
                  <LineChart data={chartData}>
                    <YAxis reversed hide domain={['dataMin', 'dataMax']} />
                    <Line type="monotone" dataKey="value" stroke="#9333ea" strokeWidth={2} dot={false} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
          <div className="space-y-1">
            {data.topResults.slice(0, 5).map((row) => {
              const isOwn = row.orgId === data.ownOrgId;
              return (
                <div
                  key={row.orgId}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${isOwn ? 'bg-primary-50 font-semibold text-primary-900' : 'text-gray-700'}`}
                >
                  <span className="w-5 text-gray-400">{row.position}</span>
                  <span className="flex-1 truncate">{isOwn ? businessName : row.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!loading && (
        <div className="mt-6 pt-6 border-t border-gray-100">
          <p className="text-sm font-semibold text-gray-900 mb-3">Позиции по отслеживаемым запросам</p>
          {!keywordPositions || keywordPositions.keywords.length === 0 ? (
            <p className="text-sm text-gray-400">
              Отметьте ключевые слова для отслеживания на странице «Ключевые слова» — тогда здесь
              появится точная позиция по каждому запросу вместо одной общей категории.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 text-xs">
                    <th className="py-1.5 pr-4 font-medium">Запрос</th>
                    <th className="py-1.5 pr-4 font-medium text-center">Позиция</th>
                    <th className="py-1.5 font-medium text-center">Динамика</th>
                  </tr>
                </thead>
                <tbody>
                  {keywordPositions.keywords.map((k) => (
                    <tr key={k.keywordId} className="border-t border-gray-50">
                      <td className="py-1.5 pr-4 text-gray-700">{k.keyword}</td>
                      <td className="py-1.5 pr-4 text-center font-medium text-gray-900">
                        {k.position !== null ? `#${k.position}` : '—'}
                      </td>
                      <td className="py-1.5 text-center">
                        <KeywordDelta delta={k.delta} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-gray-100">
        <Button variant="link" onClick={() => navigate(`/client/${businessId}/analytics`)}>
          Смотреть всех конкурентов <ArrowRight className="w-3 h-3" />
        </Button>
      </div>
    </Card>
  );
}
